import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { datadogRum } from '@datadog/browser-rum';
import { angularPlugin } from '@datadog/browser-rum-angular';
import Config from 'config/config';
import posthog from 'posthog-js';

import { AppModule } from './app/app.module';
import { AppSession } from './app/interfaces/app-session';
import {
  hasPendingConnectToken,
  stripConnectTokenFromUrl
} from './connect-token';
import {
  RedirectFlag,
  SESSION_BOOTSTRAP_DEGRADED_ACTION,
  SessionLoadResult,
  bootProviders,
  loadSession,
  purgeRetiredCredentialKeys,
  startApp,
  waitForFlagWithin
} from './session-bootstrap';
import {
  PortalRouting,
  buildVendorPortalDestinationUrl,
  buildVendorRedirectUrl,
  companyIdFromPath,
  hasAlreadyHandedOff,
  markHandedOff,
  matchesRedirectAllowlist,
  shouldRedirectAuthenticatedVendor,
  vendorIdFromSession
} from './vendor-redirect';

// The head script in index.html has normally taken a Connect link's token out
// of the address bar already, which makes this strip a no-op. It is the fallback
// for a blocked or failed script: Datadog RUM and PostHog below both record the
// page URL when they initialise. Both calls are best effort. Reading
// `localStorage` throws when storage is blocked, and a throw here would stop the
// app starting.
try {
  stripConnectTokenFromUrl(window.location, window.history);
} catch {
  // Best effort.
}
try {
  purgeRetiredCredentialKeys(localStorage);
} catch {
  // Best effort.
}

const {
  production,
  postHog,
  datadog,
  viveApiUrl,
  vendorsAppUrl,
  enableVendorAppRedirect
} = Config.getEnvironmentConfig();

if (production) {
  enableProdMode();
}

if (datadog) {
  // Initialize RUM before bootstrap so route/view tracking covers the whole app
  // lifecycle. The Angular plugin wires router views (provideDatadogRouter() in
  // AppModule); user identification is driven from AuthService.identify().
  // allowedTracingUrls stitches these RUM sessions to the backend APM traces
  // (the API is same-origin, served by the same Express server).
  datadogRum.init({
    applicationId: datadog.applicationId,
    clientToken: datadog.clientToken,
    site: datadog.site,
    service: datadog.service,
    env: datadog.env,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: 'mask',
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    allowedTracingUrls: [
      {
        match: window.location.origin,
        propagatorTypes: ['datadog', 'tracecontext']
      }
    ],
    plugins: [angularPlugin({ router: true })]
  });
}

if (postHog) {
  // VPDW-2595: localStorage persistence so PostHog does not set a non-HttpOnly
  // cookie.
  posthog.init(postHog.publicKey, {
    api_host: postHog.host,
    person_profiles: 'always',
    defaults: '2025-05-24',
    persistence: 'localStorage'
  });
}

const REDIRECT_FLAG_WAIT_MS = 3000;
const PORTAL_ROUTING_TIMEOUT_MS = 2000;
const VENDOR_APP_REDIRECT_FLAG = 'vendor-app-redirect';

// Starts now, ahead of and independent of the PostHog flag wait.
const sessionReady: Promise<SessionLoadResult> = loadSession({
  apiUrl: viveApiUrl,
  // The WARN: this app has no console logging, so it is a RUM custom action.
  onDegraded: (reasons) =>
    datadogRum.addAction(SESSION_BOOTSTRAP_DEGRADED_ACTION, { reasons })
});

function readRedirectFlag(): RedirectFlag {
  return {
    flagEnabled: posthog.isFeatureEnabled(VENDOR_APP_REDIRECT_FLAG) === true,
    payload:
      (posthog.getFeatureFlagPayload(VENDOR_APP_REDIRECT_FLAG) as
        string[] | null) ?? null
  };
}

/**
 * The `vendor-app-redirect` rollout inputs, or null if PostHog has not answered
 * within 3s. This bound applies to the flag wait and nothing else: startApp()
 * still waits for the session.
 */
function waitForRedirectFlag(): Promise<RedirectFlag | null> {
  if (!postHog) {
    // Development: PostHog is not configured — use a localStorage flag to toggle
    // the redirect so developers can test the full flow without a live PostHog
    // connection.
    //   Enable:  localStorage.setItem('vendor-app-redirect', 'true')  → reload
    //   Disable: localStorage.removeItem('vendor-app-redirect')        → reload
    return Promise.resolve({
      flagEnabled: localStorage.getItem(VENDOR_APP_REDIRECT_FLAG) === 'true',
      payload: null
    });
  }
  return waitForFlagWithin(
    (ready) => posthog.onFeatureFlags(ready),
    readRedirectFlag,
    REDIRECT_FLAG_WAIT_MS
  );
}

/**
 * The signed-in vendor's redirect inputs, bounded by an AbortController so a
 * slow API cannot hold the page. Any non-2xx or timeout → null → no redirect
 * this load; the in-app guard re-checks on navigation.
 */
async function fetchPortalRouting(
  vendorId: number
): Promise<PortalRouting | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PORTAL_ROUTING_TIMEOUT_MS);
  try {
    const response = await fetch(
      `${viveApiUrl}/api/v3/vendors/${vendorId}/portal-routing`,
      { credentials: 'include', signal: controller.signal }
    );
    if (!response.ok) {
      return null;
    }
    return (await response.json()) as PortalRouting;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The vive-vendors redirect for this load, or null to stay.
 *  1. Signed-out registration/login URL → URL-based redirect (CompanyId read
 *     from the path, matched against the flag payload).
 *  2. Signed-in vendor → fetch routing inputs and apply the sticky/flag gate.
 *     vive-vendors reads the same session cookie, so the URL carries nothing.
 */
async function resolveRedirectUrl(
  vendorsBaseUrl: string,
  { flagEnabled, payload }: RedirectFlag,
  session: AppSession | null
): Promise<string | null> {
  const { pathname, search } = window.location;

  if (flagEnabled) {
    const urlTarget = buildVendorRedirectUrl(vendorsBaseUrl, pathname, search);
    if (urlTarget) {
      const companyId = companyIdFromPath(pathname);
      const candidates = companyId ? [companyId] : [];
      if (matchesRedirectAllowlist(candidates, payload)) {
        return urlTarget;
      }
    }
  }

  // Durable per-tab guard prevents a legacy ⇄ vive-vendors redirect loop.
  if (hasAlreadyHandedOff(sessionStorage)) {
    return null;
  }
  const vendorId = vendorIdFromSession(session);
  if (vendorId === null) {
    return null;
  }
  const routing = await fetchPortalRouting(vendorId);
  if (
    !routing ||
    !shouldRedirectAuthenticatedVendor(routing, flagEnabled, payload)
  ) {
    return null;
  }
  return buildVendorPortalDestinationUrl(vendorsBaseUrl, '/');
}

function bootstrapApp(result: SessionLoadResult): void {
  platformBrowserDynamic(bootProviders(result))
    .bootstrapModule(AppModule)
    .catch((err) => console.error(err));
}

void startApp({
  sessionReady,
  // Master kill switch: one flip disables ALL redirects, including sticky ones.
  // A pending Connect exchange skips them too — the login page is about to
  // replace this session with the link's.
  redirectsEnabled:
    Boolean(enableVendorAppRedirect && vendorsAppUrl) &&
    !hasPendingConnectToken(),
  waitForRedirectFlag,
  resolveRedirectUrl: (flag, session) =>
    vendorsAppUrl
      ? resolveRedirectUrl(vendorsAppUrl, flag, session)
      : Promise.resolve(null),
  redirect: (url) => {
    // Durable per-tab guard prevents a legacy ⇄ vive-vendors redirect loop.
    markHandedOff(sessionStorage);
    window.location.replace(url);
  },
  bootstrap: bootstrapApp
});
